import { useAuth0 } from '@auth0/auth0-react';
import type { TrackingProps } from '@seek/tealium-adapter';
import { type ReactNode, createContext, useContext, useMemo } from 'react';

import { usePermissions } from '../auth.tsx';
import { useSelf } from '../self.ts';

import { useEnvironmentConfig } from '#src/hooks/environment.tsx';
import { createAnalyticsProvider } from '#src/utils/analyticsClient.ts';
import { getSiteTrackingData } from '#src/utils/getSiteTrackingData.ts';

interface TrackingProviderProps {
  children: ReactNode;
}

export interface TrackingFunctionProps {
  eventName: string;
  data: TrackingProps;
}

export interface Analytics {
  trackView: (input: TrackingFunctionProps) => void;
  trackLink: (input: TrackingFunctionProps) => void;
}

const TrackingContext = createContext<Analytics>({
  trackView: () => {},
  trackLink: () => {},
});

export const TrackingProvider = ({ children }: TrackingProviderProps) => {
  const trackingEnabled = useEnvironmentConfig().trackingEnabled;
  const { isAuthenticated, user } = useAuth0();
  const { isImpersonating, integrationPartnerUuid } = usePermissions();
  const { self } = useSelf();

  const value = useMemo(() => {
    const userData = {
      isLoggedIn: isAuthenticated,
      userId: user?.sub || null,
      integrationPartnerId: integrationPartnerUuid || null,
      isImpersonation: Boolean(isImpersonating),
      integrationPartnerName: self?.partner.name || null,
    };

    const trackView = (input: TrackingFunctionProps) => {
      createAnalyticsProvider(trackingEnabled).view(input.eventName, {
        ...input.data,
        ...getSiteTrackingData(),
      } as TrackingProps);
    };

    const trackLink = (input: TrackingFunctionProps) => {
      createAnalyticsProvider(trackingEnabled).link(input.eventName, {
        ...input.data,
        ...getSiteTrackingData(),
        ...userData,
      } as TrackingProps);
    };

    return { trackView, trackLink };
  }, [
    integrationPartnerUuid,
    isAuthenticated,
    isImpersonating,
    self?.partner.name,
    trackingEnabled,
    user?.sub,
  ]);

  return (
    <TrackingContext.Provider value={value}>
      {children}
    </TrackingContext.Provider>
  );
};

export const useTracking = () => {
  const context = useContext(TrackingContext);
  if (!context) {
    throw new Error('useTracking must be used within a TrackingProvider');
  }
  return context;
};
